import { AbstractControl, NgForm, UntypedFormGroup } from '@angular/forms';
import { AddressFormField, AddressFormType, DPV_CODE } from '@cart-checkout/checkout/data-access/enums';
import { EventEmitter, Injectable } from '@angular/core';
import { IAvsResponse, IAvsResponseV2 } from '@cart-checkout/checkout/data-access/interfaces';

@Injectable({
	providedIn: 'root'
})
export class CheckoutFormService {
	/**
	 * @description
	 * Updates form values with values from AVS response and check if same day zip code needs to change
	 *
	 * @param addressForm - Form reference
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param avsResponse - Reponse from V1 or V2 AVS API
	 * @param sddAddress - Informs whether address is for same day delivery
	 * @param oldZipCodeValue - Used to determine if AVS suggested zip code differs from submitted zip code
	 * @returns void
	 */
	public handleAvsValidation(
		addressForm: NgForm,
		formName: AddressFormType,
		avsResponse: IAvsResponse | IAvsResponseV2,
		sddAddress = false,
		oldZipCodeValue?: string
	): NgForm {
		const addresses = addressForm.controls.addresses;

		// Update the form with what AVS responded with as a verified address
		addresses.patchValue(
			{
				[formName]: {
					address: avsResponse.verifiedAddress.addressLines[0],
					address2:
						typeof avsResponse.verifiedAddress.addressLines[1] === 'string'
							? avsResponse.verifiedAddress.addressLines[1]
							: null, // Clear address2 if not a string because it would have been added into line 1 by AVS
					city: avsResponse.verifiedAddress.city,
					state: avsResponse.verifiedAddress.state,
					zipcode: avsResponse.verifiedAddress.zipCode
				}
			},
			{ emitEvent: false }
		);

		// If address is same day and AVS suggested zip code differs from inputted zip then set form needSDDEligibilityCheck to true
		// and set the opposing form to false
		if (
			sddAddress &&
			this.formZipCodeValue(addressForm.form, formName) != null &&
			oldZipCodeValue &&
			!this.formZipCodeValue(addressForm.form, formName).includes(oldZipCodeValue)
		) {
			const sameDayShippingOtherForm = formName === AddressFormType.SHIPPING ? AddressFormType.BILLING : AddressFormType.SHIPPING;
			addressForm.value.addresses[formName].needSDDEligibilityCheck = true;
			if (addressForm.value.addresses[sameDayShippingOtherForm]) {
				addressForm.value.addresses[
					sameDayShippingOtherForm
				].needSDDEligibilityCheck = false;
			}
		}

		// Manually emit statusChanges to preserve analytics because setting emitEvent to false prevents valueChanges and statusChanges events
		(addresses.statusChanges as EventEmitter<any>).emit(addresses.status);

		// Set the avsChecked flag to TRUE
		addressForm.value.addresses[formName].avsChecked = true;
		return addressForm;
	}

	/**
	 * @description
	 * Sets the appropriet form field errors and sets the form avsChecked to false
	 *
	 * @param addressForm - Form reference
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param dpvCode - Code returned from AVS service that informs us what went wrong
	 * @returns void
	 */
	public handleAvsRejection(
		addressForm: NgForm,
		formName: string,
		dpvCode: number,
		rejectedFormField?: AddressFormField
	): void {
		// Use the DPV code to determine which form field needs which error
		switch (dpvCode) {
			case DPV_CODE.NO_DPV:
			case DPV_CODE.INVALID_ADDRESS:
				this.addressLine1Control(addressForm.form, formName).setErrors({ required: true });

				this.zipCodeControl(addressForm.form, formName).setErrors({ required: true });

				if (this.addressLine2Value(addressForm.form, formName)) {
					this.addressLine2Control(addressForm.form, formName).setErrors({ required: true });
				}
				break;

			case DPV_CODE.INVALID_APT_NUMBER:
				this.addressLine2Control(addressForm.form, formName).setErrors({ required: true });
				break;

			case DPV_CODE.MISSING_APT_NUMBER:
				this.addressLine2Control(addressForm.form, formName).markAsTouched(); // Need this to make the form field visible if hidden by accordion
				this.addressLine2Control(addressForm.form, formName).setErrors({ required: true });
				break;

			case DPV_CODE.DISTINCT_FORM_FIELD_ERROR:
				if (rejectedFormField === AddressFormField.SDDZIPCODE) {
					this.zipCodeControl(addressForm.form, formName).setErrors({ checkSDDZip: true });
				}
				break;
		}

		// Set the avsChecked flag to false
		addressForm.value.addresses[formName].avsChecked = false;
	}

	/**
	 * @description
	 * Gets the zip code form value from the nested form control
	 *
	 * @param form - Form reference
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @returns string
	 */
	private formZipCodeValue(form: UntypedFormGroup, formName: string): string {
		return this.zipCodeControl(form, formName).value;
	}

	/**
	 * @description
	 * Gets the address line 2 value from the nested form control
	 *
	 * @param form - Form reference
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @returns string
	 */
	private addressLine2Value(form: UntypedFormGroup, formName: string): string {
		return this.addressLine2Control(form, formName).value;
	}

	/**
	 * @description
	 * Gets the address line 1 control from the nested form control
	 *
	 * @param form - Form reference
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @returns AbstractControl
	 */
	private addressLine1Control(form: UntypedFormGroup, formName: string): AbstractControl {
		return form.get(`addresses.${formName}.address`);
	}

	/**
	 * @description
	 * Gets the address line 2 control from the nested form control
	 *
	 * @param form - Form reference
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @returns AbstractControl
	 */
	private addressLine2Control(form: UntypedFormGroup, formName: string): AbstractControl {
		return form.get(`addresses.${formName}.address2`);
	}

	/**
	 * @description
	 * Gets the zipcode control from the nested form control
	 *
	 * @param form - Form reference
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @returns AbstractControl
	 */
	private zipCodeControl(form: UntypedFormGroup, formName: string): AbstractControl {
		return form.get(`addresses.${formName}.zipcode`);
	}
}
